import React, { useState } from "react"
import tw, { styled } from "twin.macro"
import { Transition } from "react-transition-group"
import axios from "axios"
import { useForm } from "react-hook-form"
import { navigate } from "gatsby"

import Chevron from "../icons/chevron"
import HelpIcon from "../icons/helpIcon"
import NSNModalDetail from "./nsnModalDetail"
import NSNHelpPopup from "./nsnHelpPopup"
import Checkbox from "./checkbox"

// Key for the file upload input
const fileKey = "File"

const EnrolForm: React.FC = () => {
  const [showingNSNModal, setShowingNSNModal] = useState<boolean>(false)
  const [createNSN, setCreateNSN] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<String | undefined>(undefined)
  const [completedSecondary, setCompletedSecondary] = useState<boolean>(false)

  const { register, handleSubmit, errors, formState, getValues } = useForm({})

  const onSubmit = async (data: any) => {
    if (!completedSecondary) {
      setSubmitError(
        "You must confirm that you are at least 16 years old and no longer at school."
      )

      return
    }

    // Create the form data
    var form = new FormData()

    for (var key in data) {
      if (key == fileKey) {
        // we only want the first file
        form.append(fileKey, data[fileKey][0])

        if (data[fileKey][0].size > 5000000) {
          setSubmitError(
            "The file you are trying to upload is greater than 5MB, please upload a smaller file."
          )
          return
        }

        continue
      }

      form.append(key, data[key])
    }

    try {
      // Submit the form
      const result = await axios({
        method: "post",
        url: "/intro-automotive-engineering/doenrolment/",
        data: form,
        headers: { "Content-Type": "multipart-form-data" }
      })

      // If it has an error key, there is an error
      if (result.data.error) {
        setSubmitError(result.data.error)
      } else {
        // Otherwise navigate to the success page
        navigate("/enrolSuccess")
      }
    } catch (ex) {
      setSubmitError(`There was a problem submitting the form. ${ex}`)
    }
  }

  return (
    <>
      <FormContainer
        onSubmit={handleSubmit(onSubmit)}
        method="post"
        encType="multipart/form-data"
      >
        <Section>
          <Row>
            <InputFull
              ref={register}
              name="FirstName"
              placeholder="First name"
              required
              autoComplete="given-name"
            />
          </Row>
          <Row>
            <InputFull
              ref={register}
              name="MiddleName"
              placeholder="Middle name/s"
              autoComplete="additional-name"
            />
          </Row>
          <Row>
            <InputFull
              ref={register}
              name="LastName"
              placeholder="Last name"
              required
              autoComplete="family-name"
            />
          </Row>
          <Row>
            <SelectWrapper>
              <Select ref={register} name="Gender" required>
                <option value="">Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="diverse">Diverse</option>
              </Select>
            </SelectWrapper>
          </Row>
          <Row>
            <Label>Date of birth</Label>
          </Row>
          <Row>
            <div css={[tw`flex flex-row items-center justify-center w-full`]}>
              <DobInputWrapper css={[tw`w-3/5`]}>
                <SelectWrapper>
                  <Select ref={register} name="DOBDay" required>
                    <option value="">Day</option>
                    {Array.from(Array(31).keys()).map(i => (
                      <option key={i} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </Select>
                </SelectWrapper>
              </DobInputWrapper>
              <DobInputWrapper>
                <SelectWrapper>
                  <Select ref={register} name="DOBMonth" required>
                    <option value="">Month</option>
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </Select>
                </SelectWrapper>
              </DobInputWrapper>
              <SelectWrapper>
                <Select ref={register} name="DOBYear" required>
                  <option value="">Year</option>
                  <YearOptions />
                </Select>
              </SelectWrapper>
            </div>
          </Row>
          <Row>
            <label css={[tw`flex flex-row font-sans text-sm cursor-pointer mt-2`, (submitError && !completedSecondary)&& tw`text-red-600`]}>
              <Checkbox
                // required
                checked={completedSecondary}
                onChange={(e: any) => {
                  setCompletedSecondary(e.target.checked)
                }}
              />
              I confirm I am at least 16 years old and no longer at school
            </label>
          </Row>
        </Section>
        <Section>
          <Row>
            <InputFull
              ref={register}
              type="email"
              name="Email"
              placeholder="Email address"
              required
            />
          </Row>
          <Row>
            <InputFull
              ref={register({
                required: true,
                validate: {
                  emailEqual: value =>
                    value === getValues().Email ||
                    "Email confirmation does not match email address.",
                },
              })}
              type="email"
              name="emailConfirmation"
              placeholder="Confirm email address"
              required
            />
            {errors.emailConfirmation && (
              <p css={[tw`font-sans text-red-600 mt-2 mb-2`]}>
                {errors.emailConfirmation.message}
              </p>
            )}
          </Row>
          <Row>
            <InputFull
              ref={register}
              name="Phone"
              placeholder="Mobile phone"
              required
            />
          </Row>
        </Section>

        <Section>
          <Row>
            <Label>Home postal address</Label>
          </Row>
          <Row>
            <InputFull
              ref={register}
              name="StreetAddressOne"
              placeholder="Street 1"
              required
              autoComplete="address-line1"
            />
          </Row>
          <Row>
            <InputFull
              ref={register}
              name="StreetAddressTwo"
              placeholder="Street 2"
              autoComplete="address-line2"
            />
          </Row>
          <Row>
            <InputFull
              ref={register}
              name="Suburb"
              placeholder="Suburb"
              required
              autoComplete="province"
            />
          </Row>
          <Row>
            <InputFull
              ref={register}
              name="City"
              placeholder="City"
              required
              autoComplete="city"
            />
          </Row>
          <Row>
            <InputFull
              ref={register}
              name="Postcode"
              placeholder="Postcode"
              required
              autoComplete="postcode"
            />
          </Row>
        </Section>

        <Section>
          <Row>
            <Label>
              {createNSN ? "Create a new NSN number" : "NSN number"}
            </Label>
          </Row>
          {!createNSN ? (
            <>
              <HorizontalRow>
                {showingNSNModal ? (
                  <NSNHelpPopup
                    closeButtonClicked={() => setShowingNSNModal(false)}
                  >
                    <NSNModalDetail
                      closeButtonClicked={() => setShowingNSNModal(false)}
                      createButtonClicked={() => {
                        setShowingNSNModal(false)
                        setCreateNSN(true)
                      }}
                    />
                  </NSNHelpPopup>
                ) : undefined}

                <InputFull
                  ref={register}
                  name="NationalStudentNumber"
                  placeholder="NSN number"
                  required
                />
                <button
                  css={[tw`focus:outline-none`]}
                  onClick={e => {
                    setShowingNSNModal(true)
                    e.preventDefault()
                  }}
                >
                  <HelpIcon title="What is my NSN number?" />
                </button>
              </HorizontalRow>
              <Description>
                Don't know your NSN number?{" "}
                <button
                  css={[tw`underline cursor-pointer focus:outline-none`]}
                  onClick={e => {
                    setShowingNSNModal(true)
                    e.preventDefault()
                  }}
                >
                  Click here
                </button>
              </Description>
            </>
          ) : undefined}
          <Transition in={createNSN} timeout={500}>
            {state => (
              <>
                <div
                  css={[
                    tw`border border-white rounded p-2 mb-2 transition hidden duration-500 ease-in bg-black`,
                    state == "entering" && tw`bg-red-600 block`,
                    state == "entered" && tw`bg-black block`,
                  ]}
                >
                  <Description>
                    Select an image (jpg, png) or PDF document containing a copy
                    of your passport or birth certificate. Maximum file size is
                    5 MB.
                  </Description>
                  {createNSN ? (
                    <FileInput
                      name={fileKey}
                      ref={register}
                      type="file"
                      accept="image/png, image/jpeg, application/pdf"
                      required
                    />
                  ) : undefined}
                </div>
                <div
                  css={[
                    tw`transition-opacity hidden duration-500`,
                    state == "entering" && tw`opacity-0 block`,
                    state == "entered" && tw`opacity-100 block`,
                  ]}
                >
                  <Description>
                    Already have an NSN number?{" "}
                    <button
                      css={[tw`underline cursor-pointer focus:outline-none`]}
                      onClick={e => {
                        setCreateNSN(false)
                        e.preventDefault()
                      }}
                    >
                      Click here
                    </button>
                  </Description>
                </div>
              </>
            )}
          </Transition>
        </Section>

        <Section>
          {Object.keys(errors).length > 0 ? (
            <SubmitError>
              The form contains input errors. Please check and submit again.
            </SubmitError>
          ) : undefined}
          {submitError ? <SubmitError>{submitError}</SubmitError> : undefined}
          <div css={[tw`flex flex-row justify-center items-center`]}>
            <SubmitButton type="submit" disabled={formState.isSubmitting}>
              {formState.isSubmitting ? "SUBMITTING..." : "SEND"}
            </SubmitButton>
          </div>
        </Section>

        <Section>
          <Row>
            <div css={[tw`flex flex-col justify-center w-full`]}>
              <a
                css={[
                  tw`font-sans text-sm text-gray-400 underline text-center`,
                ]}
                href="https://www.mito.org.nz/privacy-and-copyright/"
                target="_blank"
              >
                Privacy statement
              </a>
            </div>
          </Row>
        </Section>
      </FormContainer>
    </>
  )
}

// Help test async behaviour in the form
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

{

}

const YearOptions: React.FC = () => {
  const minAge = 16 // minimum age for signup
  let year = new Date().getFullYear() - minAge  // current year minus the age limit (16)
  const maxYears = 100  // total number of year options e.g. 2006 - 1907

  const arrayKeys = Array(maxYears).keys()  // returns array iterator
  const yearsArray = Array.from(arrayKeys) // array of [0...99]

  return (
    <>
      {yearsArray.map(i => (
        <option key={i} value={year - i}>{year - i}</option>
      ))}
    </>
  )
}

const FormContainer = styled.form`
  width: 18.75rem;
`

const Section = styled.div`
  ${tw`mb-8`}
`

const Row = styled.div`
  ${tw`flex flex-col w-full mb-2`}
`

const HorizontalRow = styled.div`
  ${tw`relative flex flex-row w-full mb-2`}
`

const Label = styled.label`
  ${tw`block font-sans whitespace-no-wrap text-sm my-1`}
`

const SubmitError = styled.p`
  ${tw`text-red-600 my-4 font-sans`}
`

const Description = styled.p`
  ${tw`block font-sans text-xs text-gray-200 mb-4`}
`

const InputFull = styled.input(({ error }) => [
  tw`w-full font-sans p-2 focus:outline-none text-black rounded placeholder-gray-500 border-2 border-transparent focus:border-brand-yellow mr-2 last:mr-0`,
  error && tw`border-red-600`,
])

const DobInputWrapper = styled.div`
  ${tw`mr-2 w-full`}
`

const Select = styled.select(({ error }) => [
  tw`block w-full font-sans p-2 focus:outline-none appearance-none text-black rounded placeholder-gray-800 border-2 border-transparent focus:border-brand-yellow bg-white`,
  error && tw`border-red-600`,
])

const SelectWrapper: React.FC = ({ children }) => (
  <div css={[tw`inline-block relative w-full text-black`]}>
    {children}
    <div
      css={[
        tw`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700`,
      ]}
    >
      <Chevron />
    </div>
  </div>
)

const FileInput = styled.input`
  &::-webkit-file-upload-button {
    ${tw`font-licenz bg-brand-yellow rounded text-black px-4 py-1 uppercase hover:text-black hover:bg-white focus:outline-none border border-2 border-transparent focus:border-white cursor-pointer`}
  }
  ${tw`mb-4 focus:outline-none`}
`

const SubmitButton = styled.button`
  ${tw`font-licenz bg-brand-yellow rounded text-black px-4 py-1 uppercase hover:text-black hover:bg-white focus:outline-none border border-2 border-transparent focus:border-white`}
`

export default EnrolForm
